import type { IConfComponents, IConfFormularios, IConfPersonasPaciente } from "@/modules/Administracion/interfaces";
import type { ICitaAdmision } from "@/modules/Admision/interfaces";
import type { IFormComponent, IFormularios, IFormxtab } from "@/modules/Configuracion/interfaces/IFormComponent";
import { computed, nextTick, onUnmounted, ref, type ComputedRef, type Ref } from "vue";
import type { ICitaTechnologiasSalud } from "../interfaces";
import { helpers, required } from "@vuelidate/validators";
import { desMapDocuments, mapTabForm, openModal, sinSimbolos, validateOrdersBeforeClear, generarUuid } from "@/modules/Core/usings/generales";
import type { IConfMotivos } from "@/modules/Configuracion/interfaces/tablas/IConfMotivos";
import { admisionService, formCompValueService, formularioServicios } from "@/services";
import { useOdontograma } from "./useOdontograma";
import { useStore } from "vuex";
import useVuelidate from "@vuelidate/core";
import { useRoute } from "vue-router";
import { useGraficas } from "./useGraficas";
interface AsistencialFormulariosReturn {
    admision: Ref<ICitaAdmision | undefined>
    documento: Ref<IFormularios | null>
    documentoTabs: Ref<IFormxtab[]>
    formCamposTabValidar: Ref<IConfComponents[]>
    sexo: ComputedRef<string|undefined>
    edadAnios: ComputedRef<number>
    edadDias: ComputedRef<number>
    edadMeses: ComputedRef<number>
    edadSemanas: ComputedRef<number>
    escolaridad: ComputedRef<string | undefined>
    fechaNacimiento: ComputedRef<string | undefined>
    technologia?: ICitaTechnologiasSalud
    rules: Record<string, unknown>
    isFinalizable: Ref<boolean>
    isSaving: Ref<boolean>
    isLoading: Ref<boolean>
    isEdit: Ref<boolean>
    motivoReapertura: Ref<string>
    tabs: Ref<{ nombre: string, index: number, isOdontology?: boolean }[] | undefined>
    activeTab: Ref<number | undefined>
    isPreForm: Ref<boolean>
    getValidacion: (item: IFormComponent) => { $message: string, $params: Record<string, unknown> } | null
    validarAndSaveAtencion: (prev?: boolean, pag?: number | null) => Promise<void>
    agregarItemMultiple: (item: IFormComponent, padre?: string | null) => void
    eliminarItemMultiple: (item: IFormComponent, padre?: string | null) => void
    callBacKOnClearForm: (motivo: string) => Promise<void>
    handleClearForm: () => Promise<void>
    cambiarTab: (regresar?: boolean, pag?: number | null) => Promise<void>
    setCittId: (id: number) => void
    getCittId: () => number
    reloadData: () => Promise<void>
    finalizarAtencion: () => Promise<void>

}
export const skeletonHistori = {
  filas: [
    {
      cols: [
        {
          style: 'width:80%',
          lines: [
            { style: 'width:100%', class: 'h-[100px]' }
          ]
        },
        {
          style: 'width:20%;padding-left:5px',
          lines: [
            { style: 'width:100%', class: 'h-[100px]' }
          ]
        }
      ]
    },
    {
      cols: [
        {
          style: 'width:100%;margin:2px',
          lines: [{ style: 'width:100%', class: 'h-20' }]
        }
      ]
    },
    {
      cols: [
        {
          style: 'width:100px;margin:2px',
          lines: [{ style: 'width:100%', class: 'h-10' }]
        },
        {
          style: 'width:100px;margin:2px',
          lines: [{ style: 'width:100%', class: 'h-10' }]
        },
        {
          style: 'width:100px;margin:2px',
          lines: [{ style: 'width:100%', class: 'h-10' }]
        },
        {
          style: 'width:100px;margin:2px',
          lines: [{ style: 'width:100%', class: 'h-10' }]
        },
        {
          style: 'width:100px;margin:2px',
          lines: [{ style: 'width:100%', class: 'h-10' }]
        }
      ]
    },
    {
      cols: [
        {
          style: 'width:100%',
          lines: [{ style: 'width:100%', class: 'h-[400px]' }]
        },
      ]
    }
  ]
}
const documentoTabs =  ref([] as IFormxtab[])
const formCamposTabValidar = ref([] as IConfComponents[])
let technologia;
const isFinalizable = ref(false)
const isSaving = ref(false)
const isLoading = ref(true)
const isEdit = ref(false)
const motivoReapertura = ref('')
const tabs = ref<{ nombre: string, index: number, isOdontology?: boolean }[]>()
const admision = ref<ICitaAdmision>()
const documento = ref<IFormularios | null>(null)
const fechaNacimiento = computed(() => {
  if (
    admision.value &&
    admision.value.paciente &&
    admision.value.technologias &&
    admision.value.technologias.length > 0
  ) {
    const fechaNacimientoStr = admision.value.paciente.coppFechanacimiento
    return fechaNacimientoStr
  }
  return '1990-01-01'
})
const edadAnios = computed(() => {
  if (
    admision.value &&
    admision.value.paciente &&
    admision.value.technologias &&
    admision.value.technologias.length > 0
  ) {
    const fechaInicioStr = technologia.cittFechaInicio
    const fechaNacimientoStr = admision.value.paciente.coppFechanacimiento
    if (fechaInicioStr && fechaNacimientoStr) {
      const fechaInicio = new Date(fechaInicioStr)
      const fechaNacimiento = new Date(fechaNacimientoStr)
      const edad = fechaInicio.getFullYear() - fechaNacimiento.getFullYear()
      return edad
    }
  }
  return 0
})
const edadDias = computed(() => {
  if (
    admision.value &&
    admision.value.paciente &&
    admision.value.technologias &&
    admision.value.technologias.length > 0
  ) {
    const fechaInicioStr = technologia.cittFechaInicio
    const fechaNacimientoStr = admision.value.paciente.coppFechanacimiento
    if (fechaInicioStr && fechaNacimientoStr) {
      const fechaInicio = new Date(fechaInicioStr)
      const fechaNacimiento = new Date(fechaNacimientoStr)
      const diferenciaMs = fechaInicio.getTime() - fechaNacimiento.getTime()
      const dias = Math.floor(diferenciaMs / (1000 * 60 * 60 * 24)) // milisegundos a días
      return dias
    }
  }
  return 0
})
const edadMeses = computed(() => {
  if (
    admision.value &&
    admision.value.paciente &&
    admision.value.technologias &&
    admision.value.technologias.length > 0
  ) {
    const fechaInicioStr = technologia.cittFechaInicio
    const fechaNacimientoStr = admision.value.paciente.coppFechanacimiento
    if (fechaInicioStr && fechaNacimientoStr) {
      const fechaInicio = new Date(fechaInicioStr)
      const fechaNacimiento = new Date(fechaNacimientoStr)

      let meses =
        (fechaInicio.getFullYear() - fechaNacimiento.getFullYear()) * 12 +
        (fechaInicio.getMonth() - fechaNacimiento.getMonth())

      // Ajuste si el día del mes de inicio es anterior al de nacimiento
      if (fechaInicio.getDate() < fechaNacimiento.getDate()) {
        meses -= 1
      }

      return meses
    }
  }
  return 0
})
const edadSemanas = computed(() => {
  if (
    admision.value &&
    admision.value.paciente &&
    admision.value.technologias &&
    admision.value.technologias.length > 0
  ) {
    const fechaInicioStr = technologia.cittFechaInicio
    const fechaNacimientoStr = admision.value.paciente.coppFechanacimiento
    if (fechaInicioStr && fechaNacimientoStr) {
      const fechaInicio = new Date(fechaInicioStr)
      const fechaNacimiento = new Date(fechaNacimientoStr)
      const diferenciaMs = fechaInicio.getTime() - fechaNacimiento.getTime()
      const semanas = Math.floor(diferenciaMs / (1000 * 60 * 60 * 24 * 7)) // milisegundos a semanas
      return semanas
    }
  }
  return 0
})
const escolaridad = computed(() => {
  if (admision.value && admision.value.paciente) {
    return admision.value.paciente.educativo?.comaNombrecorto
  }
  return ''
})

const rules = {
  formCamposTabValidar: {
    $each: helpers.forEach({
      value: {
        required: (value, vm) => vm.isRequired ? required.$validator(value, {}, vm) : true,
        sinSimbolos
      },
    })
  }
}
let cittId = 0
const activeTab = ref<number>()
const sexo = computed(() => {
  if (admision.value && admision.value.paciente) {
      return admision.value.paciente.sexo?.comaNombrecorto
  }
  return ''
})
const isPreForm = ref(false)
const oldTab = ref<{ nombre: string, index: number, isOdontology?: boolean } | null>(null)
export function useAsistencialFormularios(tipo: string): AsistencialFormulariosReturn {
  const route = useRoute()
  isPreForm.value = (route.query?.preForm ? true : false)

  // Aquí puedes agregar la lógica para tu formulario
    const store = useStore()

    const { validateOdontograma, limpiarOrigen, limpiarAtencion } = useOdontograma('historia')


const v$ = useVuelidate(rules, { formCamposTabValidar }, { $lazy: true })
const setCittId = (id: number) => {
  // Punto de entrada de un formulario (vistas de Consulta/Procedimiento/Otros
  // servicios, FormularioDefault y TramitarFormModal). El estado del odontograma
  // vive en el store, que es de nivel app y no se limpia solo: al abrir OTRA
  // atencion se arranca en limpio para no heredar el diligenciamiento ni los
  // dientes ausentes de la anterior.
  // Se compara contra el actual a proposito: varios componentes de la MISMA
  // atencion vuelven a llamar setCittId y no deben borrar lo que se lleva marcado.
  if (cittId !== id) {
    limpiarOrigen()
    limpiarAtencion(id)
  }
  cittId = id
}
const getCittId = () => {
  return cittId
}
const tiposMotivosReapertura: Record<string, string> = {
  HISTORIA: 'REAPERTURA HISTORIA CLINICA',
  PROCEDIMIENTO: 'REAPERTURA PROCEDIMIENTO'
}
const rutasRedirect = {
  HISTORIA: '/asistenciales/Consulta/View/' + cittId,
  PROCEDIMIENTO: '/asistenciales/Procedimiento/View/' + cittId,
  LABORATORIO: '/asistenciales/Laboratorio/View/' + admision.value?.ciadId,
  'OTROS SERVICIOS': '/asistenciales/OtrosServicios/View/' + cittId
}

const redirigirTrasGuardar = () => {
  if (route.name === 'AsistencialVacunacionView') window.location.reload()
  else window.location.href = rutasRedirect[tipo as keyof typeof rutasRedirect]
}
const motivosClear = {
  HISTORIA: 'LIMPIAR HISTORIA',
  PROCEDIMIENTO: 'LIMPIAR PROCEDIMIENTO',
  LABORATORIO: 'LIMPIAR LABORATORIO',
  'OTROS SERVICIOS': 'LIMPIAR OTROS SERVICIOS'
}
const reloadData = async () => {
  const { data } = await admisionService.getAdmisionByTechId(cittId, { withPacienteSnapshot: true })
  const datosAdmision = data.body
  admision.value = datosAdmision
  if (admision.value.technologias && admision.value.technologias.length > 0) {
    technologia = admision.value.technologias.find(item => item.cittId == cittId)
    if(tipo != 'LABORATORIO'){
      store.commit('core/setHeader', {
        title: technologia?.contratoxservicio?.servicio?.coseNombre,
        subTitles: admision.value.technologias.filter(item => item.cittAdmisionado == 1 && item.cittId != cittId).map(item  => tipo == 'HISTORIA' ? item.contratoxservicio?.servicio?.coseNombre:item.contratoxservicio?.cup?.cocuNombre )
      })
    }
    const { coseId, cocuId } = technologia?.contratoxservicio || {}
    const { data: dataFormulario } = await formularioServicios.view(0, {
      withComponents: false,
      coseId: coseId,
      cocuId: cocuId,
      codoEstado: 1,
      codoTipo: tipo })
    if (!dataFormulario.body) {
      documento.value = null
      isLoading.value = false
      return
    }
    documento.value = {} as IFormularios
    documento.value.codoId = dataFormulario.body.codoId
    documento.value.codoScript = dataFormulario.body.codoScript
    isEdit.value = technologia?.cittEstado == 'TRAMITADO'
    let formulario: IConfFormularios | ICitaTechnologiasSalud | null  = null

    if(['TRAMITADO', 'EN PROCESO', 'PRE CONSULTA' ].some(e => e == technologia?.cittEstado)){
      const { data: dataGuardado } = await formCompValueService.document(cittId, { isForm: true, onlyTabs: true })
      documento.value.isNewSave = false
      documento.value.technologiasIds = [technologia?.cittId as number]
      formulario = dataGuardado.body
    } else {
      const { data: dataNuevo } = await formularioServicios.view(0, {
        withComponents: true,
        coseId: coseId,
        cocuId: cocuId,
        ...(tipo == 'HISTORIA' || tipo == 'OTROS SERVICIOS' ? {
          coppTipoSexo: admision.value.paciente?.coppTipoSexo,
          copeId: admision.value.paciente?.copeId,
          coppFechanacimiento: admision.value.paciente?.coppFechanacimiento,
          withAfter: !['TRAMITADO', 'EN PROCESO'].some(e => e == technologia?.cittEstado),
          cittFechainicio: technologia?.cittFechaInicio
        }: {}),
       codoEstado: 1, codoTipo: tipo, state: 1, insertDefault: true, isPreForm: isPreForm.value, cittId: cittId, zip: true, isForm: true })
      if(!dataNuevo.body){
        window.notificacion.report('info','No se  ha encontrado formulario','El servicio de la atencion no tiene un formulario asignado, contactar con soporte.')
        documento.value = null
        isLoading.value = false
        return
      }
      documento.value.isNewSave = true
      documento.value.technologiasIds = [technologia?.cittId as number]// admision.value.technologias.filter(item => item.cittAdmisionado == 1).map(item  => item.cittId as number) as number[]
      formulario = dataNuevo.body
    }
    documento.value.tabs = []
    const tabsWithComponentsFormFiltered = formulario.tabs?.filter(e => e.components && e.components.length > 0)

    const tabsMaps = tabsWithComponentsFormFiltered?.map(mapTabForm) as IFormxtab[]

    tabs.value = []
    for (const element of tabsMaps) {
      element.index =  tabsMaps.indexOf(element)
      element.isOdontology = element.components?.some(e => e.odontograma)
      element.cftaNombreOriginal = element.cftaNombre.replaceAll(' ', '_')
      tabs.value.push({nombre: element.cftaNombre, index: element.index, isOdontology: element.isOdontology})
    }
    documentoTabs.value = tabsMaps
    if(tipo === 'HISTORIA'){
      tabs.value.push({nombre: 'ORDENES', index: tabs.value.length})
      const { setGraficas } = useGraficas((admision.value.paciente as IConfPersonasPaciente)) 
      await setGraficas((admision.value.paciente?.sexo?.comaNombrecorto as string), (admision.value.paciente?.daisOld as number))
    }
    if(tabs.value && tabs.value.length > 0) activeTab.value = tabs.value[0].index

    if(admision.value && admision.value.paciente) admision.value.paciente.daisOld = edadDias.value
  }
  isLoading.value = false
  await cargarScripts()
}
const cargarScripts = async () => {
  await nextTick()
  if (documento.value && documento.value.codoScript) {
    try {
      new Function(documento.value.codoScript)();
    } catch (error) {
      window.notificacion.notificacion('failure', "Error ejecutando el script base del formulario.")
      console.error("Error ejecutando el script:", error);
    }
  }
}
const getValidacion = (item: IFormComponent) => {
  const campo = formCamposTabValidar.value.find(e => e.id == item.id)
  if (!campo) return null
  const index = formCamposTabValidar.value.indexOf(campo)
  const error = v$.value.formCamposTabValidar.$each.$response?.$errors[index]?.value
  if (!error) return null
  return error
}
const finalizarAtencion = async () => {
  if (tipo == 'HISTORIA' && !isFinalizable.value) {
    window.notificacion.notificacion('failure', "Debes diligenciar todos los campos obligatorios, causa externa, finalidad y diagnosticos para poder finalizar.")
    return
  }
  window.notificacion.loading().circle('Guardando...')
  isSaving.value = true
  try {
    if(admision.value?.technologias){
      await admisionService.finishTech(technologia?.cittId as number)
      window.notificacion.notificacion('success', "Atencion finalizada correctamente")
      redirigirTrasGuardar()
    }
  } catch (error) {
    console.log(error)
  } finally {
    window.notificacion.loading().remove()
  }
  isSaving.value = false
}
const validarBeforeSaving = async () => {
  const components = desMapDocuments(documentoTabs.value.filter(item => item.index == activeTab.value).map(item => item.components).flat() as IConfComponents[])
  v$.value.formCamposTabValidar.$reset()
  formCamposTabValidar.value = components.map(comp => ({ ...comp, isRequired: comp.required }))
  const isFormCorrect = await v$.value.formCamposTabValidar.$validate()
  if (!isFormCorrect) {
    window.notificacion.notificacion('failure', "Por favor, corrija los errores en el formulario")
    window.notificacion.loading().remove()
    return false
  }
  return true
}
const validarAndSaveAtencion = async (prev = false, pag: number | null = null) => {
  window.notificacion.loading().circle('Guardando...')
  if (!isPreForm.value && !await validarBeforeSaving()) return
  isSaving.value = true
  try {
    /* documentoTabs.value.forEach(item => {
      if(item.components) item.components = removeDuplicatesByName(item.components)
    }) */
    const formTab =  JSON.stringify({ technologiasIds: documento.value?.technologiasIds, tabs: isPreForm.value ? documentoTabs.value : documentoTabs.value.filter(item => item.index == activeTab.value), preForm: isPreForm.value, motivoReapertura: motivoReapertura.value, tipoMotivoReapertura: tiposMotivosReapertura[tipo] })
    const gz = await formCompValueService.gzipString(formTab)
    await formCompValueService.saveDocument(admision.value?.ciadId as number,gz)
    if(documento.value){
      documento.value.isNewSave = false
    }
    // await reloadData()
    if(document.getElementById('HistoriaJson')) (document.getElementById('HistoriaJson') as HTMLInputElement).value = JSON.stringify(documentoTabs.value)
    window.notificacion.notificacion('success', "Campos registrados correctamente")
    if (isPreForm.value) redirigirTrasGuardar()
    if (!isPreForm.value && tabs.value && tabs.value[tabs.value.length-1].index != activeTab.value) {
      cambiarTab(prev, pag)
    } else if (!isPreForm.value) {
      await finalizarAtencion()
    }
  } catch (error) {
    console.log(error)
  } finally {
    window.notificacion.loading().remove()
    isSaving.value = false
    scrollToTop()
  }
}
const cambiarTab = async (regresar: boolean = false, pag: number | null = null) => {
  if (tabs.value && activeTab.value !== undefined && tabs.value.find(e => e.nombre == 'ORDENES')) {
    oldTab.value = null
    if(regresar){
      oldTab.value = tabs.value.find(e => e.index == activeTab.value) as { nombre: string, index: number, isOdontology?: boolean }
    }
    const tabData = tabs.value.find(e => e.index == activeTab.value)
    if(!tabData?.isOdontology){
      efectuarCambioTab(regresar, pag)
    } else {
      const { noValidos, isValid } = validateOdontograma()
      if (isValid) {
        efectuarCambioTab(regresar, pag)
      } else {
        window.notificacion.confirmacion(
        'Items sin diligenciar!',
        `Los siguentes items de odontología no tienen registros:
        <br><strong>${noValidos}</strong><br>
        <strong>Desea continuar sin diligenciarlos?</strong>`,
        () => {
          efectuarCambioTab(regresar, pag)
        },
        () => { },
        'rgb(37 99 235 / var(--tw-text-opacity, 1))',
        false,
        1000
        )
      }
    }
  }
}
const efectuarCambioTab = async (regresar: boolean = false, pag: number | null = null) => {
    if (typeof(pag) === 'number'){
      activeTab.value = pag
      await cargarScripts()
    } else {
      if (tabs.value && activeTab.value !== undefined) {
        const indexTabActive = activeTab.value
        const nextTab = indexTabActive + 1
        const prevTab = indexTabActive - 1
        activeTab.value = regresar ? prevTab : nextTab
        await cargarScripts()
      }
    }
}
const handleClearForm = async () => {
  const currentTech = admision.value?.technologias?.find(t => t.cittId === cittId)

  if (tipo == 'HISTORIA' && currentTech && !validateOrdersBeforeClear(currentTech)) {
    window.notificacion.notificacion('warning', 'No se puede limpiar la historia porque tiene órdenes que ya han sido gestionadas (agendadas o admitidas).')
    return
  }
  openModal('form-clear-hc', false)
}
const callBacKOnClearForm = async (motivo:string) => {
  window.notificacion.loading().circle()

  const payload: IConfMotivos = {
    cmotMotivo: motivo,
    cmotTabla: 'tbl_cita_technologiassalud',
    cmotReferenciaId: cittId,
    cmotTipo: motivosClear[tipo]
  }
  await formCompValueService.clearHistory(cittId, payload)

  window.notificacion.loading().remove()
  window.notificacion.notificacion('success', "Ha sido limpiado con exito.")
  window.location.reload()
}
// Un ítem nuevo debe nacer en blanco: antes se clonaba el anterior con todo y
// valores, así que el usuario podía guardar por error los datos clínicos del
// ítem previo repetidos.
const limpiarValores = (comp: IFormComponent) => {
  if (comp.typeComponent !== 'SECTION' && comp.typeComponent !== 'CONTAINER') {
    // LISTA_SQL nunca guarda un valor fijo (mismo criterio que el backend).
    comp.value = comp.typeComponent === 'LISTA_SQL'
      ? null
      : (comp.defaultValue ?? (comp.typeComponent === 'CHECKBOX' ? 'NO' : null))
    // searchValue está tipado como `string | undefined` (no acepta null).
    comp.searchValue = undefined
  }
  for (const hijo of (comp.components || [])) limpiarValores(hijo)
}
const agregarItemMultiple = (item: IFormComponent, padre?: string | null) => {
  const newItem = JSON.parse(JSON.stringify(item)) as IFormComponent
  limpiarValores(newItem)
  for (const tab of documentoTabs.value) {
    if(tab.components){
      if (!padre) {
        const result = procesarAgregarItem(newItem, tab.components)
        if (result) break
      }
      const result = recorrerComponentes(tab.components, newItem, padre)
      if (result) break
    }
  }
}
const eliminarItemMultiple = (item: IFormComponent, padre?: string | null) => {
  for (const tab of documentoTabs.value) {
    if(tab.components){
      if (!padre && tab.components.some(comp => comp.name === item.name)) {
        tab.components = tab.components.filter(comp => comp.name !== item.name)
        // Sin renumerar quedaban huecos (ITEM_1, ITEM_3) y los nombres dejaban
        // de corresponder con el orden mostrado.
        actualizarComponentes(item, tab.components)
        break
      } else {
         const result = recorrerComponentes(tab.components, item, padre, true)
         if (result) break
      }
    }
  }
}
const recorrerComponentes = (componentes: IFormComponent[], item: IFormComponent, padre?: string | null, eliminar?: boolean) => {
  const compPadre = componentes.find(componente => componente.name == padre)
  if(compPadre){
      if (!eliminar) {
         const result = procesarAgregarItem(item, compPadre.components as IFormComponent[])
         if (result) return true
      } else if (compPadre.components && compPadre.components.some(comp => comp.name === item.name)) {
         // Antes comparaba con `!==`: con un solo hijo no borraba nada y con
         // varios entraba aunque el ítem buscado no estuviera en la lista.
         compPadre.components = compPadre.components.filter(comp => comp.name !== item.name)
         actualizarComponentes(item, compPadre.components as IFormComponent[])
         return true
      }
      return false
  }
  for (const element of componentes) {
    if (element.components) {
      const result = recorrerComponentes(element.components, item, padre, eliminar)
      if (result) return true
    }
  }
}
const procesarAgregarItem = (item: IFormComponent, components: IFormComponent[]) => {
  const codigo = item.name.split('_ITEM_')[0]
  const index = components.findIndex(comp => comp.name === item.name)
  if(index === -1) return false
  const nuevoName = `${codigo}_ITEM_${ generarUuid()}`;
  item.name = nuevoName;
  components.splice(index + 1, 0, item)
  actualizarComponentes(item, components)
  return true;
}
// Actualiza los padres en profundidad; las subsecciones múltiples conservan su índice.
const renumerarDescendientes = (componentes: IFormComponent[], indice: number, padre: string) => {
  for (const comp of (componentes || [])) {
    const esMultiple = comp.typeComponent === 'SECTION' && comp.multi
    if (!esMultiple) {
      comp.name = `${comp.name.split('_ITEM_')[0]}_ITEM_${indice}`
      if (comp.item !== undefined) comp.item = indice
    }
    comp.componentId = padre
    if (comp.components && comp.components.length) {
      renumerarDescendientes(comp.components, esMultiple ? (comp.item ?? indice) : indice, comp.name)
    }
  }
}
const actualizarComponentes = (item: IFormComponent, components: IFormComponent[]) => {
  let cant = 1;
  const codigo = item.name.split('_ITEM_')[0];
  for (const element of components) {
    // El prefijo completo evita renumerar otro grupo que contenga el código.
    const isNombre = element.name.startsWith(`${codigo}_ITEM_`);
    if (isNombre) {
      element.name = `${codigo}_ITEM_${cant}`;
      element.item = cant;
      renumerarDescendientes(element.components || [], cant, element.name);
      cant = cant + 1;
    }
  }
}
const scrollToTop = () => {
  const historiaClinicaElement = document.getElementById('historia_clinica')
  const topValue = historiaClinicaElement ? historiaClinicaElement.offsetTop : 0

  window.scrollTo({
    top: topValue,
    behavior: 'smooth'
  })
}

const resetState = () => {
  admision.value = undefined
  documento.value = null
  documentoTabs.value = []
  formCamposTabValidar.value = []
  technologia = null
  tabs.value = []
  isEdit.value = false
  isPreForm.value = false
  isFinalizable.value = false
  isSaving.value = false
  isLoading.value = true
  activeTab.value = 0
  isPreForm.value = false
}
onUnmounted(() => {
  if(!oldTab.value || (oldTab.value && oldTab.value.nombre !== 'ORDENES')) {
    resetState()
  }
})
 return {
    admision,
    documento,
    documentoTabs,
    formCamposTabValidar,
    sexo,
    fechaNacimiento,
    edadAnios,
    edadDias,
    edadMeses,
    edadSemanas,
    escolaridad,
    technologia,
    rules,
    isFinalizable,
    isSaving,
    isLoading,
    activeTab,
    tabs,
    isEdit,
    motivoReapertura,
    isPreForm,
    validarAndSaveAtencion,
    agregarItemMultiple,
    eliminarItemMultiple,
    callBacKOnClearForm,
    handleClearForm,
    cambiarTab,
    setCittId,
    getCittId,
    reloadData,
    finalizarAtencion,
    getValidacion
  }
 }
